import type { EstimateLineItem, ID } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { updateEstimateNodeItem } from '@store/reducers/estimates/actions'
import type { AppState } from '@store/types'
import cx from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { toFloat } from '@/helpers'
import css from './Estimates.module.scss'

type Props = {
  nodeId: ID
  focusedInput: Record<string, boolean> | null
  disabled: boolean
  onChangeInputWidth: (id: string, value: string) => void
  onFocusedInput: (v: { [id: string]: boolean } | null) => void
}

export const EstimatesItem: React.FC<Props> = ({
  nodeId,
  focusedInput,
  disabled,
  onChangeInputWidth,
  onFocusedInput,
}) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const lineItems = useSelector(
    migrationProjectSelector.getProjectEstimateLinkedLineItems
  )

  const nodeEstimates = useSelector(
    migrationProjectSelector.getProjectNodeEstimates(nodeId)
  )

  const lineItemsColumns: EstimateLineItem[][] = lineItems.reduce<
    EstimateLineItem[][]
  >((acc, item, index) => {
    const columnIndex = Math.floor(index / 3)

    if (!acc[columnIndex]) {
      acc[columnIndex] = []
    }

    const estimate = nodeEstimates.find((e) => e.lineItemId === item.id)

    const value = estimate ? estimate.value : 0
    acc[columnIndex].push({
      ...item,
      value,
    })

    return acc
  }, [])

  const unit = useSelector(migrationProjectSelector.getProjectEstimateUnits)

  const [inputValues, setInputValues] = useState<Record<string, string> | null>(
    null
  )

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.select()
  }, [focusedInput])

  const handleOnFocus = (id: string) => {
    onFocusedInput({ [id]: true })
  }

  const handleOnBlur = () => {
    onFocusedInput(null)
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target

    const hours = Number.isNaN(Math.sign(toFloat(value))) ? 0 : toFloat(value)

    setInputValues((prev) => ({ ...prev, [id]: String(value) }))
    onChangeInputWidth(id, value)

    sendGtmEvent(
      GtmTriggerName.ESTIM_HOUR_PAGE,
      'Change estimate hours in page on artboard'
    )

    dispatch(
      updateEstimateNodeItem({
        nodeId,
        lineItemId: id,
        value: hours,
      })
    )
  }

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const { id } = e.currentTarget

      const index = lineItems.findIndex((e) => e.id === id)
      const nextItem = lineItems[index + 1]

      if (nextItem) {
        handleOnFocus(nextItem.id)
      } else {
        e.currentTarget.blur()
      }
    }
  }

  const getInputValue = (id: string) => {
    if (!inputValues) {
      return 0
    }

    return focusedInput && focusedInput[id]
      ? inputValues[id]
      : `${inputValues[id]}${unit}`
  }

  return (
    <>
      {/* // @todo find another way to show tooltip */}
      {/* {isEqualToZero && !estimatesWasOpen && !isReadOnly && ( */}
      {/*   <div className={css.tooltip}> */}
      {/*     <TooltipIcon className={css.arrow} /> */}
      {/*     Start by adding hours to&nbsp;page */}
      {/*   </div> */}
      {/* )} */}
      {lineItemsColumns.map((column, i) => (
        <div
          key={i}
          className={cx(css.column, {
            [css.disabled]: disabled,
          })}
        >
          {column.map((estimate, index) => (
            <React.Fragment key={estimate.id}>
              {focusedInput && focusedInput[estimate.id] ? (
                <div className={css.wrapper}>
                  <input
                    type="text"
                    className={css.input}
                    value={getInputValue(estimate.id)}
                    style={{
                      color: estimate.color || 'var(--color-code-blue)',
                    }}
                    ref={inputRef}
                    id={estimate.id}
                    data-next-id={column[index + 1]?.id}
                    maxLength={3}
                    onChange={handleOnChange}
                    onBlur={handleOnBlur}
                    onKeyDown={handleOnKeyDown}
                  />
                </div>
              ) : (
                <div className={css.wrapper}>
                  <div
                    className={css.value}
                    style={{
                      color: estimate.color || 'var(--color-code-blue)',
                    }}
                    onClick={() => handleOnFocus(estimate.id)}
                    id={estimate.id}
                  >
                    {estimate.value}
                    {unit}
                  </div>
                </div>
              )}
              {index !== 2 && (
                <div className={css.divider} key={`${estimate.id}/${index}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      ))}
    </>
  )
}
